import React, { useEffect, useRef, useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Ticker from './Ticker';
import Breadcrumbs from './Breadcrumbs';
import HomeView from './views/HomeView';
import SummaryView from './views/SummaryView';
import DetailView from './views/DetailView';
import AdminPanel from './admin/AdminPanel';
import HelpModal from './modals/HelpModal';
import AuthModal from './modals/AuthModal';
import SettingsModal from './modals/SettingsModal';

const Dashboard = () => {
  const { currentView } = useDashboard();
  const { isAdmin } = useAuth();
  const mainRef = useRef(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  // Listen for URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Listen for custom navigation events
    window.addEventListener('navigation', handleLocationChange);
    
    // Check pathname periodically (fallback)
    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        handleLocationChange();
      }
    }, 100);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('navigation', handleLocationChange);
      clearInterval(interval);
    };
  }, [currentPath]);
  
  // Check if we're on admin route
  const isAdminRoute = currentPath.includes('/admin');

  useEffect(() => {
    const updateMainMargin = () => {
      const header = document.querySelector('header');
      const ticker = document.querySelector('.ticker-wrap');
      const breadcrumb = document.querySelector('.breadcrumb-bar');
      
      if (mainRef.current) {
        let totalHeight = 140; // Default fallback
        
        if (header && breadcrumb) {
          // Use getBoundingClientRect for more accurate measurements
          const headerRect = header.getBoundingClientRect();
          const breadcrumbRect = breadcrumb.getBoundingClientRect();
          
          const headerHeight = headerRect.height || header.offsetHeight || 70;
          const breadcrumbHeight = breadcrumbRect.height || breadcrumb.offsetHeight || 38;
          
          // Only add ticker height if ticker exists (not on admin page)
          let tickerHeight = 0;
          if (ticker && !isAdminRoute) {
            const tickerRect = ticker.getBoundingClientRect();
            tickerHeight = tickerRect.height || ticker.offsetHeight || 32;
          }
          
          totalHeight = headerHeight + tickerHeight + breadcrumbHeight;
          
          // Add small buffer to prevent overlap
          totalHeight += 2;
          
          // Ensure minimum spacing
          if (totalHeight < 100) totalHeight = 140;
        }
        
        mainRef.current.style.marginTop = `${totalHeight}px`;
        mainRef.current.style.paddingTop = '1rem';
      }
    };

    // Update on mount and resize with multiple attempts
    updateMainMargin();
    
    const timeouts = [
      setTimeout(updateMainMargin, 50),
      setTimeout(updateMainMargin, 100),
      setTimeout(updateMainMargin, 200),
      setTimeout(updateMainMargin, 500),
      setTimeout(updateMainMargin, 1000)
    ];
    
    window.addEventListener('resize', updateMainMargin);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateMainMargin, 100);
    });
    
    // Use ResizeObserver to watch for changes in header, ticker, and breadcrumb heights
    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateMainMargin();
      });
      
      const header = document.querySelector('header');
      const ticker = document.querySelector('.ticker-wrap');
      const breadcrumb = document.querySelector('.breadcrumb-bar');
      
      if (header) resizeObserver.observe(header);
      if (ticker && !isAdminRoute) resizeObserver.observe(ticker);
      if (breadcrumb) resizeObserver.observe(breadcrumb);
    }

    return () => {
      window.removeEventListener('resize', updateMainMargin);
      window.removeEventListener('orientationchange', updateMainMargin);
      timeouts.forEach(timeout => clearTimeout(timeout));
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentView, isAdminRoute]);

  return (
    <div className="dashboard">
      <Header />
      {!isAdminRoute && <Ticker />}
      <Breadcrumbs />
      
      <main ref={mainRef}>
        {isAdminRoute && isAdmin() ? (
          <AdminPanel />
        ) : (
          <>
            {currentView === 'home' && <HomeView />}
            {currentView === 'summary' && <SummaryView />}
            {currentView === 'detail' && <DetailView />}
          </>
        )}
      </main>

      <HelpModal />
      <AuthModal />
      <SettingsModal />
    </div>
  );
};

export default Dashboard;

